import { type ColumnDef } from "@tanstack/react-table";
import { ChevronIcon } from "vega-icons";

import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { type ExpandableRowConfig } from "../../types";
import { TABLE_COLUMN_WIDTHS } from "../constants";

export const createExpandableColumn = <TData,>(
  config: ExpandableRowConfig<TData>,
  expandedRows: Set<string>,
  onToggleRow: (rowId: string) => void,
  data: TData[],
): ColumnDef<TData, unknown> | null => {
  const hasExpandableRows = data.some(config.isExpandable);

  if (!hasExpandableRows) {
    return null;
  }

  return {
    id: "expandable",
    header: () => null,
    cell: ({ row }) => {
      const rowData = row.original;
      const rowId = config.getRowId(rowData);
      const canExpand = config.isExpandable(rowData);
      const expanded = expandedRows.has(rowId);

      if (canExpand) {
        return (
          <div
            data-testid={`expand-chevron-${rowId}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleRow(rowId);
            }}
            role="button"
            tabIndex={0}
          >
            <ChevronIcon
              className={cnTwMerge(
                "text-text/secondary defaultTransition",
                expanded ? "rotate-180" : "rotate-0",
              )}
            />
          </div>
        );
      }

      return null;
    },
    enableSorting: false,
    enableHiding: false,
    size: TABLE_COLUMN_WIDTHS.EXPANDABLE,
    align: "left",
    className: "pr-0 pl-4",
  };
};
